import { TruckIcon } from '@heroicons/react/24/outline'
import { useState, useEffect } from 'react'

import Breadcrumb from '@/components/navigation/Breadcrumb'
import { ShipmentNotification, ShipmentNotificationParams } from '@/interfaces/shipmentNotification'
import { FiltersRow } from './filters/FiltersRow'
import { getColumns } from './table/Columns'
import { DataTable } from './table/DataTable'
import { TableContextProvider } from './table/TableContext'
import NewShipmentNotificationModal from './components/NewShipmentNotificationModal'
import { useCreateShipmentNotification } from './hooks/useCreateShipmentNotification'
import { toast } from 'react-toastify'
import { useUpdateShipmentNotification } from './hooks/useUpdateShipmentNotification'
import { useShipmentNotificationFilters } from './filters/hooks/useShipmentNotificationFilters'
import { getStatusLabel } from './filters/constants/statusOptions'

export default function ShipmentNotificationList() {
  const [showEditNotification, setShowEditNotification] = useState(false)
  const [showDeleteNotification, setShowDeleteNotification] = useState(false)
  const [showNewNotification, setShowNewNotification] = useState(false)
  const [notificationToEdit, setNotificationToEdit] = useState<ShipmentNotification>()
  const [notificationToDelete, setNotificationToDelete] = useState<number>()
  const [showAllNotificationsMessage, setShowAllNotificationsMessage] = useState(false)
  const [showNoNotificationsMessage, setShowNoNotificationsMessage] = useState(false)

  // Hook para crear notificación
  const createNotificationMutation = useCreateShipmentNotification()
  
  // Hook para actualizar notificación
  const updateNotificationMutation = useUpdateShipmentNotification()

  // Hook para filtros
  const { filters, updateStatus, clearAllFilters } = useShipmentNotificationFilters()

  // Ocultar mensaje cuando el usuario cambia los filtros manualmente
  useEffect(() => {
    if (showAllNotificationsMessage && filters.status.includes('all')) {
      setShowAllNotificationsMessage(false)
    }
  }, [filters.status, showAllNotificationsMessage])

  const handleSaveNotification = async (data: ShipmentNotificationParams) => {
    if (notificationToEdit) {
      // Update existing notification
      try {
        await updateNotificationMutation.mutateAsync({
          id: notificationToEdit.id,
          data
        })
        setNotificationToEdit(undefined)
        setShowEditNotification(false)
        
        // Show success message
        toast.success(`Notificación actualizada a: ${getStatusLabel(data.status || notificationToEdit.status)}`, {
          autoClose: 3000
        })
        
        // Check if the updated notification might be hidden by current filters
        const currentFilters = filters
        const updatedNotificationStatus = data.status || notificationToEdit.status
        
      } catch (error) {
        toast.error('Error al actualizar la notificación')
      }
    } else {
      // Create new notification
      try {
        const result = await createNotificationMutation.mutateAsync(data)
        
        if (result.success) {
          toast.success(result.message || 'Notificación creada exitosamente')
          // Close modal and reset all state
          handleCloseModal()
        } else {
          toast.error(result.message || 'Error al crear la notificación')
        }
      } catch (error) {
        toast.error('Error al crear la notificación')
      }
    }
  }

  const handleNewNotification = () => {
    setNotificationToEdit(undefined)
    setShowEditNotification(false)
    setShowNewNotification(true)
  }

  const handleCloseModal = () => {
    setShowNewNotification(false)
    setShowEditNotification(false)
    setNotificationToEdit(undefined)
  }

  const handleCloseNew = () => {
    handleCloseModal()
  }

  const handleCloseEdit = () => {
    handleCloseModal()
  }

  const handleCloseDelete = () => {
    setShowDeleteNotification(false)
    setNotificationToDelete(undefined)
  }

  const handleShowAllNotifications = () => {
    updateStatus(['CREATED', 'PENDING_TO_ARRIVE'])
    setShowAllNotificationsMessage(false)
    toast.info('Mostrando notificaciones por defecto (CREATED y PENDING_TO_ARRIVE)')
  }

  const handleShowAllStatuses = () => {
    updateStatus(['all'])
    setShowAllNotificationsMessage(false)
    toast.info('Mostrando todas las notificaciones (puede ser lento)')
  }

  return (
    <div>
      <Breadcrumb
        icon={<TruckIcon className="h-6 text-gray-500" />}
        paths={[{ name: 'Logística' }, { name: 'Notificación de embarque' }]}
      />
      
      {/* Mensaje para mostrar todas las notificaciones */}
      {showAllNotificationsMessage && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  Notificación actualizada. Por defecto solo se muestran CREATED y PENDING_TO_ARRIVE para optimizar la conexión.
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0 flex gap-2">
              <button
                onClick={handleShowAllNotifications}
                className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm hover:bg-blue-700"
              >
                Por defecto
              </button>
              <button
                onClick={handleShowAllStatuses}
                className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700"
              >
                Ver todas
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje cuando no hay notificaciones después de actualización */}
      {showNoNotificationsMessage && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  No se encontraron notificaciones. Por defecto solo se cargan CREATED y PENDING_TO_ARRIVE. Usa filtros para ver otros estados.
                </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={() => setShowNoNotificationsMessage(false)}
                className="bg-yellow-600 text-white px-3 py-1 rounded-md text-sm hover:bg-yellow-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="w-full">
        <FiltersRow />
      </div>
      <TableContextProvider>
        <DataTable
          columns={getColumns(
            setShowEditNotification,
            setNotificationToEdit,
            setShowDeleteNotification,
            setNotificationToDelete
          )}
          showEditNotification={showEditNotification}
          showDeleteNotification={showDeleteNotification}
          onClose={handleCloseEdit}
          onCloseDelete={handleCloseDelete}
          notificationToEdit={notificationToEdit}
          notificationToDelete={notificationToDelete}
          onNewNotification={handleNewNotification}
          showAllNotificationsMessage={showAllNotificationsMessage}
          showNoNotificationsMessage={showNoNotificationsMessage}
          onShowAllNotifications={handleShowAllNotifications}
          onShowAllStatuses={handleShowAllStatuses}
          onCloseNoNotificationsMessage={() => setShowNoNotificationsMessage(false)}
        />
      </TableContextProvider>

      {/* Modal para crear/editar notificación */}
      <NewShipmentNotificationModal
        isOpen={showNewNotification || showEditNotification}
        onClose={handleCloseNew}
        notificationToEdit={notificationToEdit}
        onSave={handleSaveNotification}
        isLoading={createNotificationMutation.isPending || updateNotificationMutation.isPending}
      />
    </div>
  )
}
